/**
 * The details of a product: name, brand, category, type, prices and the level
 * to reorder at. One form, used wherever a product is created or edited.
 *
 * ## Why this exists
 *
 * Until 13 October 2026 a product made from "Add stock" was created with a
 * name and a reorder level and nothing else. Its brand, category, type, cost
 * and retail price could only be filled in from the Import & export page,
 * which nobody would think to look on. Emmanuel hit exactly that in testing:
 * "when I add a stock and mistakenly leave the page how do I carry on to edit
 * it?" The same form now opens from Add stock and from any product's drawer.
 *
 * Money is typed in pounds and leaves here as integer pence (`poundsToPence`).
 */
import { formatAmount, parseAmount } from '@inventory-tracker/core';
import { useState } from 'react';
import type { Classification, CreateProductInput } from '../lib/api.js';
import { c, field, label as labelStyle, primaryButton } from '../theme.js';
import { CategoryField } from './CategoryField.js';
import { penceToPounds, poundsToPence, smallButton } from './format.js';

export interface ProductFormValues {
  name: string;
  brand: string | null;
  category: string | null;
  classificationId: string | null;
  unitCostPence: number;
  retailPricePence: number | null;
  minLevel: number;
  /** What one pack holds, in hundredths. 100 when the product is used whole. */
  packContentsHundredths: number;
  /** "units", "ml". Null when it is used whole. */
  contentUnit: string | null;
}

interface Props {
  initial: Partial<ProductFormValues>;
  classifications: Classification[];
  categories: string[];
  submitLabel: string;
  onSubmit: (values: ProductFormValues) => Promise<void>;
  onCancel: () => void;
}

/** What `POST /products` and `PATCH /products/:id` both accept. */
export function toProductBody(v: ProductFormValues): CreateProductInput {
  return {
    name: v.name,
    ...(v.brand ? { brand: v.brand } : {}),
    ...(v.category ? { category: v.category } : {}),
    ...(v.classificationId ? { classificationId: v.classificationId } : {}),
    unitCostPence: v.unitCostPence,
    retailPricePence: v.retailPricePence,
    minLevel: v.minLevel,
    packContentsHundredths: v.packContentsHundredths,
    contentUnit: v.contentUnit,
  };
}

export function ProductForm({
  initial,
  classifications,
  categories,
  submitLabel,
  onSubmit,
  onCancel,
}: Props) {
  const [name, setName] = useState(initial.name ?? '');
  const [brand, setBrand] = useState(initial.brand ?? '');
  const [category, setCategory] = useState(initial.category ?? '');
  const [classificationId, setClassificationId] = useState(
    initial.classificationId ?? classifications[0]?.id ?? '',
  );
  const [cost, setCost] = useState(
    initial.unitCostPence === undefined ? '' : penceToPounds(initial.unitCostPence),
  );
  const [retail, setRetail] = useState(penceToPounds(initial.retailPricePence ?? null));
  const [minLevel, setMinLevel] = useState(
    initial.minLevel === undefined ? '' : String(initial.minLevel),
  );
  // Used in parts: "one vial holds 100 units". Off for anything used whole.
  const [inParts, setInParts] = useState(
    (initial.packContentsHundredths ?? 100) !== 100 || Boolean(initial.contentUnit),
  );
  const [holds, setHolds] = useState(
    initial.packContentsHundredths && initial.packContentsHundredths !== 100
      ? formatAmount(initial.packContentsHundredths)
      : '',
  );
  const [contentUnit, setContentUnit] = useState(initial.contentUnit ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A classification chosen before the list arrived falls back to the first.
  const chosenClassification = classificationId || classifications[0]?.id || '';
  const isRetail = classifications.find((cl) => cl.id === chosenClassification)?.isRetail ?? false;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (name.trim() === '') {
      setError('A product needs a name');
      return;
    }
    const costPence = cost.trim() === '' ? 0 : poundsToPence(cost);
    if (costPence === null) {
      setError('Unit cost must be a number, like 95 or 95.50');
      return;
    }
    // An empty retail price is meaningful: it means "we do not sell this".
    const retailPence = retail.trim() === '' ? null : poundsToPence(retail);
    if (retail.trim() !== '' && retailPence === null) {
      setError('Retail price must be a number, or empty if you do not sell it');
      return;
    }
    const min = minLevel.trim() === '' ? 0 : Number(minLevel);
    if (!Number.isInteger(min) || min < 0) {
      setError('Reorder level must be a whole number');
      return;
    }

    let packContentsHundredths = 100;
    let unit: string | null = null;
    if (inParts) {
      const parsed = parseAmount(holds);
      if (parsed === null) {
        setError('Say how much one pack holds, like 100 or 2.5');
        return;
      }
      if (contentUnit.trim() === '') {
        setError('Say what it is measured in, like units or ml');
        return;
      }
      packContentsHundredths = parsed;
      unit = contentUnit.trim();
    }

    setBusy(true);
    setError(null);
    try {
      await onSubmit({
        packContentsHundredths,
        contentUnit: unit,
        name: name.trim(),
        brand: brand.trim() === '' ? null : brand.trim(),
        category: category.trim() === '' ? null : category.trim(),
        classificationId: chosenClassification || null,
        unitCostPence: costPence,
        retailPricePence: retailPence,
        minLevel: min,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that');
      setBusy(false);
    }
  }

  const two: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 12,
  };
  const small = { ...field, height: 44, fontSize: 15 };

  return (
    <form onSubmit={submit} style={{ display: 'grid', gap: 12 }}>
      {error && (
        <div
          role="alert"
          style={{
            background: c.dangerBg,
            border: `1px solid ${c.dangerLine}`,
            borderRadius: 12,
            padding: '10px 14px',
            fontSize: 13.5,
            color: c.danger,
          }}
        >
          {error}
        </div>
      )}

      <div>
        <label style={labelStyle} htmlFor="pf-name">
          Product name
        </label>
        <input
          id="pf-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Azzalure 125u"
          style={small}
        />
      </div>

      <div style={two}>
        <div>
          <label style={labelStyle} htmlFor="pf-brand">
            Brand
          </label>
          <input
            id="pf-brand"
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            placeholder="Optional"
            style={small}
          />
        </div>
        <div>
          <label style={labelStyle} htmlFor="pf-category">
            Category
          </label>
          <CategoryField
            id="pf-category"
            value={category}
            onChange={setCategory}
            categories={categories}
            style={{ ...small, padding: '0 10px' }}
          />
        </div>
      </div>

      <div style={two}>
        <div>
          <label style={labelStyle} htmlFor="pf-type">
            Type
          </label>
          <select
            id="pf-type"
            value={chosenClassification}
            onChange={(e) => setClassificationId(e.target.value)}
            style={{ ...small, padding: '0 10px' }}
          >
            {classifications.map((cl) => (
              <option key={cl.id} value={cl.id}>
                {cl.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle} htmlFor="pf-min">
            Reorder when at or below
          </label>
          <input
            id="pf-min"
            value={minLevel}
            onChange={(e) => setMinLevel(e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="0"
            inputMode="numeric"
            style={small}
          />
        </div>
      </div>

      <div style={two}>
        <div>
          <label style={labelStyle} htmlFor="pf-cost">
            Unit cost (£)
          </label>
          <input
            id="pf-cost"
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            placeholder="0.00"
            inputMode="decimal"
            style={small}
          />
        </div>
        <div>
          <label style={labelStyle} htmlFor="pf-retail">
            Retail price (£)
          </label>
          <input
            id="pf-retail"
            value={retail}
            onChange={(e) => setRetail(e.target.value)}
            placeholder={isRetail ? 'What you sell it for' : 'Leave empty if not sold'}
            inputMode="decimal"
            style={small}
          />
        </div>
      </div>

      {/* For products a treatment uses part of. Stock is still counted in whole
          packs; this is what lets a recipe say "40 units" or "0.5 ml". */}
      <div style={{ background: c.appBg, borderRadius: 12, padding: '12px 14px' }}>
        <label style={{ display: 'flex', gap: 9, alignItems: 'center', fontSize: 14, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={inParts}
            onChange={(e) => setInParts(e.target.checked)}
            style={{ width: 17, height: 17, accentColor: c.green }}
          />
          A treatment can use part of one
        </label>
        {inParts ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
            <span style={{ fontSize: 14, color: c.muted }}>One pack holds</span>
            <input
              value={holds}
              onChange={(e) => setHolds(e.target.value)}
              placeholder="100"
              inputMode="decimal"
              aria-label="How much one pack holds"
              style={{ ...small, width: 90 }}
            />
            <input
              value={contentUnit}
              onChange={(e) => setContentUnit(e.target.value)}
              placeholder="units or ml"
              aria-label="What it is measured in"
              style={{ ...small, width: 130 }}
            />
          </div>
        ) : (
          <p style={{ fontSize: 13, color: c.muted, margin: '6px 0 0 26px', lineHeight: 1.5 }}>
            Tick this for a vial or syringe that is shared across treatments. Stock is still
            counted in whole packs.
          </p>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
        <button
          type="submit"
          disabled={busy}
          style={{ ...primaryButton, width: 'auto', height: 44, padding: '0 22px', opacity: busy ? 0.6 : 1 }}
        >
          {busy ? 'Saving…' : submitLabel}
        </button>
        <button type="button" onClick={onCancel} style={{ ...smallButton, height: 44, padding: '0 18px' }}>
          Cancel
        </button>
      </div>
    </form>
  );
}
